/**
 * Drop Generator — Phase 12 text ranking contracts. A campaign group's texts
 * (one SMSWiser template id each — the tracker's custom variable 6 / token_6)
 * ranked by their own results on a Flow-Optimizer-shaped formula (the hour
 * formula's shape: groups with their own date range, weighted), under the
 * same honest rule as hours: below the volume floor a text is UNRANKED,
 * never scored as bad. Every lever is a setting (the user's rule: "everything
 * has to be modulable"): how many drafts reuse proven texts, from how deep a
 * pool, whether fresh AI texts lean on the best prompt groups, which texts
 * are never reused.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenRule, DropGenRuleHit, DropGenTextRuleAction } from "../rules/rules.types";
import type { DropGenTagsRead } from "../rules/tags/item-tag-context";
import type {
  DropGenHourFormula,
  DropGenHourScoreBreakdownRow,
} from "../hour-profile.types";
import type { DropGenTextPolicy } from "./text-policy";

export const DROP_GEN_TEXT_RANKING_LIMITS = {
  topNMax: 50,
  excludedMax: 500,
  /** Ranked + unranked texts a preview returns at most. */
  listMax: 200,
} as const;

/** An SMSWiser template id: CRC32 of the text, 8 lowercase hex chars. */
export const TEMPLATE_ID_PATTERN = /^[0-9a-f]{8}$/;

export interface DropGenTextRankingConfig {
  /** What "good" means — the hour formula's shape and metrics. */
  formula: DropGenHourFormula;
  /** Phase 37 — proven texts on every ordinary drop (0 = drafts keep fresh AI texts only). */
  provenPerDrop: number;
  /** Reused texts come from the top N reusable ranked texts. */
  topN: number;
  /** The fresh-AI share favours the prompt groups whose texts score best. */
  leanGroups: boolean;
  /** Template ids never reused (still ranked and shown). */
  excluded: string[];
  /** Phase 19 — optional rules (retire · proven · retire the prompt group). */
  rules?: DropGenRule<DropGenTextRuleAction>[];
  /** Phase 37 — a drop kind's own mix (proven + AI texts); a kind not set follows the ordinary mix. */
  policy?: DropGenTextPolicy;
}

export interface DropGenRankedText {
  templateId: string;
  /** null when the catalog does not know the text (it can never be reused). */
  text: string | null;
  groupId: string | null;
  groupName: string | null;
  score: number | null;
  rank: number | null;
  unranked: boolean;
  uniqueVisits: number;
  visits: number;
  conversions: number;
  revenue: number;
  breakdown?: DropGenHourScoreBreakdownRow[];
  /** Every value of the text per range the formula reads (range key → the Flow Optimizer's full bag). */
  metricsByRange: Record<string, Record<string, number>>;
  excluded: boolean;
  /** Ranked, known to the catalog, one SMS long, not excluded. */
  reusable: boolean;
  /** In the top-N reuse pool. */
  inPool: boolean;
  /** Phase 19 — what the text rules said; retired = a retire rule fired. */
  ruleHits?: DropGenRuleHit[];
  retired?: boolean;
  /** Phase 25 — its Tag Rules › Texts tags (read when a rule tests one). */
  tags?: string[];
}

export interface DropGenRankedGroup {
  groupId: string;
  groupName: string;
  textCount: number;
  rankedCount: number;
  /** The group's texts summed, scored on the same formula; null under the floor. */
  score: number | null;
  rank: number | null;
  uniqueVisits: number;
  conversions: number;
  breakdown?: DropGenHourScoreBreakdownRow[];
  /** The group's texts summed, per range the formula reads. */
  metricsByRange: Record<string, Record<string, number>>;
  /** Phase 19 — a retire-the-group rule fired: fresh AI texts skip it. */
  retired?: boolean;
  ruleHits?: DropGenRuleHit[];
  /** Phase 25 — every tag any of its texts carries. */
  tags?: string[];
}

/** A date range still being read from the tracker (Binom answers 1,000 texts per page). */
export interface DropGenTextReadingRange {
  rangeKey: string;
  pages: number;
  texts: number;
  visitsRead: number;
  /** The range's total visits, when the tracker says. */
  visitsTotal: number | null;
}

export interface DropGenTextRankingPreview extends DropGenTagsRead {
  countryKey: string;
  campaignWiserId: number;
  provider: "VOLUUM" | "BINOM" | "TRES_COMMAS";
  primaryRangeKey: string;
  /** The formula these scores come from — the math is explained with it, never with a newer draft. */
  formula: DropGenHourFormula;
  /** Best-first, then unranked by volume; capped at listMax. */
  texts: DropGenRankedText[];
  groups: DropGenRankedGroup[];
  totalTexts: number;
  rankedTexts: number;
  poolSize: number;
  fetchedAt: string;
  /** Ranges still being read — while any is, the lists are empty and the UI shows the progress. */
  reading: DropGenTextReadingRange[];
  /** 15/10/2026 — the editor's preview only, when no text is ranked yet: made-up texts on this formula. */
  example?: DropGenTextRankingExample;
}

/** Made-up texts with made-up stats, ranked by the real scorer — shown, never sent. */
export interface DropGenTextRankingExample {
  texts: DropGenRankedText[];
  groups: DropGenRankedGroup[];
  totalTexts: number;
  rankedTexts: number;
  poolSize: number;
}
